// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, {type ReactNode} from 'react';

import {getFileThumbnailUrl, getFileUrl} from 'mattermost-redux/utils/file_utils';
import {type FileInfo} from 'mattermost-redux/types/files';

import FilenameOverlay from 'components/file_attachment/filename_overlay';
import Constants, {FileTypes} from 'utils/constants';
import * as Utils from 'utils/utils';

import FileProgressPreview from './file_progress_preview';

type UploadInfo = {
    name: string;
    percent?: number;
    type?: string;
}
export type FilePreviewInfo = FileInfo & UploadInfo;

type Props = {
    onRemove?: (id: string) => void;
    fileInfos: FilePreviewInfo[];
    uploadsInProgress?: string[];
    uploadsProgressPercent?: {[clientID: string]: FilePreviewInfo};
}

export default class FilePreview extends React.PureComponent<Props> {
    static defaultProps = {
        fileInfos: [],
        uploadsInProgress: [],
        uploadsProgressPercent: {},
    };

    handleRemove = (id: string) => {
        this.props.onRemove?.(id);
    }

    render() {
        const previews: ReactNode[] = [];

        this.props.fileInfos.forEach((info) => {
            const type = Utils.getFileType(info.extension);

            let className = 'file-preview post-image__column';
            let previewImage;
            const isImageWithPreview = type === FileTypes.IMAGE && info.has_preview_image;
            const isGIF = Utils.isGIFImage(info.extension);

            if (isImageWithPreview || isGIF) {
                let imageClassName = 'post-image';

                if (
                    (info.width && info.width < Constants.THUMBNAIL_WIDTH) &&
                    (info.height && info.height < Constants.THUMBNAIL_HEIGHT)
                ) {
                    imageClassName += ' small';
                } else {
                    imageClassName += ' normal';
                }

                const thumbnailUrl = isGIF ? getFileUrl(info.id) : getFileThumbnailUrl(info.id);

                previewImage = (
                    <div
                        className={imageClassName}
                        style={{
                            backgroundImage: `url(${thumbnailUrl})`,
                            backgroundSize: 'cover',
                        }}
                    />
                );
            } else {
                className += ' custom-file';
                previewImage = <div className={'file-icon ' + Utils.getIconClassName(type)}/>;
            }

            previews.push(
                <div
                    key={info.id}
                    className={className}
                >
                    <div className='post-image__thumbnail'>
                        {previewImage}
                    </div>
                    <div className='post-image__details'>
                        <div className='post-image__detail_wrapper'>
                            <div className='post-image__detail'>
                                <FilenameOverlay
                                    fileInfo={info}
                                    compactDisplay={false}
                                    canDownload={false}
                                />
                                {
                                    info.extension &&
                                    <span className='post-image__type'>
                                        {info.extension.toUpperCase()}
                                    </span>
                                }
                                <span className='post-image__size'>{Utils.fileSizeToString(info.size)}</span>
                            </div>
                        </div>
                        {this.props.onRemove && (
                            <div>
                                <a
                                    className='file-preview__remove'
                                    onClick={this.handleRemove.bind(this, info.id)}
                                >
                                    <i className='icon icon-close'/>
                                </a>
                            </div>
                        )}
                    </div>
                </div>,
            );
        });

        this.props.uploadsInProgress?.forEach((clientId) => {
            const fileInfo = this.props.uploadsProgressPercent?.[clientId];
            if (fileInfo) {
                previews.push(
                    <FileProgressPreview
                        key={clientId}
                        clientId={clientId}
                        fileInfo={fileInfo}
                        handleRemove={this.handleRemove}
                    />,
                );
            }
        });

        return (
            <div
                className='file-preview__container'
                data-qa='file_preview__container'
            >
                {previews}
            </div>
        );
    }
}
